import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `Organization` → Overview — `/organization` (plan-014 B2; design §27.15.1; FR12-TEAM-01, FR12-AUTH-07). Drawn to the
 * prototype's `overview` page (`design/WeTalk.dc.html`, the Organization block renderer): kicker, title, lede, the
 * tab strip, then three blocks — the stats (workspaces, people, balances), the workspace list ("Seeing a workspace
 * here does not open its conversations") and the profile form (name, billing timezone that "applies from the next
 * period", default language, the copyable organization id).
 *
 * EVERY AFFORDANCE IS REAL (FR12-SCR-04). A field the person may not change is shown, read-only; the save button
 * appears only when something can be saved.
 *
 * AMENDED 2026-10-06 (plan-014 SC-P, parity with the prototype): the "Plan" stat (the shared balance's plan, as the
 * Plans page derives it, for a role that reads finance), "New workspace" (B3's dialog, for a role that manages
 * workspaces), each row's action (Open → the workspace page; Restore → Archive and close) and the storefront glyph.
 *
 * AMENDED 2026-10-09 (design sync): the workspace list moved to its own tab (Organization → Workspaces, the design's
 * `ORG_PAGES.workspaces`), so Overview is the stats and the profile; no kicker; the plan's rate reads "<rate> a minute
 * of talk time".
 *
 * AMENDED 2026-10-12 (plan-017 WAPP-A, design §29.11 row 9, owner answer Q1): the rate under the plan is the
 * organization's own talk rate (`useTalkRates`) — one figure while every kind costs the same, else the range "from–to a
 * minute, by kind of conversation" (the full table is on the Dashboard and on Billing). The offer's own rate is
 * deprecated (PQ-8) and not read.
 *
 * Optimistic concurrency: the profile carries its `revision`; a save names it, and a `revision_stale` answer reloads
 * the page's facts and says so.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { holdsAccountCapability, type AccountCapability, type AccountRole } from "@wetalk/domain";
import { Button, Field, Input, Select } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { authContextQuery } from "../../../api/query.js";
import type { AccountProfileView } from "../../../api/slice/account-team.js";
import type { BillingAccountSummaryView } from "../../../api/slice/billing-account.js";
import { formatMoney } from "../../../money.js";
import { oneTalkRateOf, talkRateRangeText, useTalkRates, type TalkRateView } from "../../../api/slice/talk-rate.js";
import { currentPlanOf } from "../../finance/plans/plans-screen.js";
import { Block, BlockNote, FormPanel, OrganizationPage, StatGrid, type Outcome } from "../page/organization-page.js";

/** The page's search parameters. None: the page has one view. */
export interface AccountOverviewSearch {}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens a
 * valid page.
 */
export function accountOverviewSearchOf(_raw: Readonly<Record<string, unknown>>): AccountOverviewSearch {
  return {};
}

export interface AccountOverviewScreenProps {
  readonly search: AccountOverviewSearch;
  readonly onSearch: (next: AccountOverviewSearch) => void;
}

/** This page's query key: the organization's profile (shared with the People page's kicker). */
export const accountProfileKey = (accountId: string) => ["account-team", "profile", accountId] as const;

/** The organization the signed-in person has open, and their role in it — `null` until known or with none. */
export function useOpenAccount(): { readonly accountId: string | null; readonly accountRole: AccountRole | null; readonly loading: boolean } {
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway));
  const value = context.data;
  if (value === undefined || value === null || value.kind === "selection_required") {
    return { accountId: null, accountRole: null, loading: context.isPending };
  }
  return { accountId: value.accountId, accountRole: value.accountRole, loading: false };
}

/** Whether the open organization role holds a capability (what the page OFFERS; the API decides). */
export function roleHolds(accountRole: AccountRole | null, capability: AccountCapability): boolean {
  return accountRole !== null && holdsAccountCapability(accountRole, capability);
}

/** Every IANA zone this browser knows, for the billing timezone field. */
function timeZoneChoices(current: string | null): readonly string[] {
  const known = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  return current === null || known.includes(current) ? known : [current, ...known];
}

function FROM_DATE(): Intl.DateTimeFormat { return new Intl.DateTimeFormat(portalLocale() === "en" ? "en-GB" : "el-GR", { day: "numeric", month: "long", year: "numeric" }); }

function workspaceStats(profile: AccountProfileView): { readonly value: string; readonly note: string } {
  const archived = profile.workspace.filter((one) => one.archived).length;
  const active = profile.workspace.length - archived;
  return { value: String(profile.workspace.length), note: archived === 0 ? portalText("core", "account_admin._active_35045c", { value0: String(active) }) : portalText("core", "account_admin._active_archived_3efc79", { value0: String(active), value1: String(archived) }) };
}

function ProfileForm(props: {
  /** The open organization's id — the one the page's query is keyed by and every write names. */
  readonly accountId: string;
  readonly profile: AccountProfileView;
  readonly canWrite: boolean;
  readonly canTimezone: boolean;
  readonly onOutcome: (outcome: Outcome) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const { profile } = props;
  const [name, setName] = useState(profile.name);
  const [language, setLanguage] = useState(profile.defaultLanguageCode ?? "");
  const [zone, setZone] = useState(profile.pendingBillingTimezone ?? profile.billingTimezone ?? "");
  const packs = useQuery({ queryKey: ["account-team", "language-packs"], queryFn: () => gateway.listLanguagePacks(), staleTime: 300_000 });

  const nameChanged = props.canWrite && name.trim() !== profile.name;
  const languageChanged = props.canWrite && language !== "" && language !== (profile.defaultLanguageCode ?? "");
  const zoneTarget = profile.pendingBillingTimezone ?? profile.billingTimezone;
  const zoneChanged = props.canTimezone && zone !== "" && zone !== zoneTarget;

  const save = useMutation({
    mutationFn: async () => {
      let revision = profile.revision;
      if (nameChanged || languageChanged) {
        const changed = await gateway.changeAccountProfile(
          props.accountId,
          { ...(nameChanged ? { name: name.trim() } : {}), ...(languageChanged ? { defaultLanguageCode: language } : {}) },
          revision,
        );
        revision = changed.revision;
      }
      if (zoneChanged) await gateway.scheduleBillingTimezone(props.accountId, zone, revision);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: accountProfileKey(props.accountId) });
      props.onOutcome({
        tone: "done",
        get text() { return zoneChanged
          ? zone === profile.billingTimezone
            ? portalText("core", "account_admin.saved_the_billing_timezone_stays_as_it_is_d6e31c")
            : portalText("core", "account_admin.saved_billing_periods_move_to_from_the_next_billing_month_42e479", { value0: zone })
          : portalText("core", "account_admin.saved_9d4af6"); },
      });
    },
    onError: async (raised: Error) => {
      await queryClient.invalidateQueries({ queryKey: accountProfileKey(props.accountId) });
      props.onOutcome({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.the_profile_could_not_be_saved_dc9a8c")); } });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (nameChanged || languageChanged || zoneChanged) save.mutate();
  };

  const pending =
    profile.pendingBillingTimezone !== null && profile.pendingBillingTimezoneFrom !== null
      ? portalText("core", "account_admin.changing_to_from_6ac397", { value0: profile.pendingBillingTimezone, value1: FROM_DATE().format(new Date(profile.pendingBillingTimezoneFrom)) })
      : portalText("core", "account_admin.sets_the_boundaries_of_billing_periods_a_change_applies_from_the_d09325");

  return (
    <FormPanel onSubmit={submit} label={portalText("core", "account_admin.profile_ff4fc0")}>
      <Field label={portalText("core", "start.organization_name_9eab9e")}>
        {(control) => <Input {...control} value={name} readOnly={!props.canWrite} onChange={(event) => setName(event.target.value)} />}
      </Field>
      <Field label={portalText("core", "start.billing_timezone_02603c")} description={pending}>
        {(control) => (
          <Select {...control} value={zone} disabled={!props.canTimezone} onChange={(event) => setZone(event.target.value)}>
            {timeZoneChoices(zoneTarget).map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={portalText("core", "account_admin.default_language_852814")}>
        {(control) =>
          packs.data === undefined ? (
            <Input {...control} value={language} readOnly={!props.canWrite} onChange={(event) => setLanguage(event.target.value)} />
          ) : (
            <Select {...control} value={language} disabled={!props.canWrite} onChange={(event) => setLanguage(event.target.value)}>
              {language === "" ? <option value="">{portalText("core", "account_admin.not_set_93039e")}</option> : null}
              {packs.data.some((one) => one.code === language) || language === "" ? null : <option value={language}>{language}</option>}
              {packs.data.map((one) => (
                <option key={one.code} value={one.code}>
                  {portalLocale() === "en" ? one.name : (new Intl.DisplayNames([portalLocale()], { type: "language" }).of(one.code) ?? one.name)}
                </option>
              ))}
            </Select>
          )
        }
      </Field>
      <Field label={portalText("core", "account_admin.organization_id_facc5b")} description={portalText("core", "account_admin.quote_this_when_you_contact_support_d289c1")}>
        {(control) => (
          <span className="op-copy">
            <Input {...control} font="mono" value={profile.accountId} readOnly />
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(profile.accountId)
                  .then(() => props.onOutcome({ tone: "done", get text() { return portalText("core", "account_admin.organization_id_copied_4033f2"); } }))
                  .catch(() => props.onOutcome({ tone: "problem", get text() { return portalText("core", "account_admin.copying_is_not_allowed_here_select_the_id_and_copy_it_4cf659"); } }));
              }}
            >{portalText("core", "account_admin.copy_af74f7")}</Button>
          </span>
        )}
      </Field>
      {props.canWrite || props.canTimezone ? (
        <span className="op-form-actions">
          <Button variant="primary" type="submit" disabled={!(nameChanged || languageChanged || zoneChanged) || save.isPending}>
            {save.isPending ? portalText("core", "account_admin.saving_56a228") : portalText("core", "account_admin.save_changes_179359")}
          </Button>
        </span>
      ) : null}
    </FormPanel>
  );
}

/**
 * The plan stat's note: "<rate> a minute of talk time" while every kind costs the same, else the range by kind; empty
 * while the rates are not read (or none is priced) — never a figure the API did not send.
 */
export function planRateNoteOf(rates: readonly TalkRateView[] | null): string {
  if (rates === null) return "";
  const one = oneTalkRateOf(rates);
  if (one !== null) return portalText("core", "account_admin._a_minute_of_talk_time_7d383d", { value0: formatMoney(one.perMinute) });
  return talkRateRangeText(rates) ?? "";
}

/**
 * The Balances stat's note, the prototype's "one shared, one dedicated": the organization's shared balance and the
 * workspaces' own balances, counted from the balances the person may read. While those are not read, or they do not
 * add up to the profile's count, the note stays general ("that fund your workspaces") — never a split it cannot back.
 */
export function balanceSplitNoteOf(balances: readonly Pick<BillingAccountSummaryView, "scope" | "lifecycleState">[] | null, count: number): string {
  const general = portalText("core", "account_admin.that_fund_your_workspaces_a5c398");
  if (balances === null) return general;
  const open = balances.filter((one) => one.lifecycleState !== "retired");
  const shared = open.filter((one) => one.scope === "account").length;
  const dedicated = open.length - shared;
  if (open.length !== count || shared > 1 || open.length === 0) return general;
  if (shared === 1) {
    if (dedicated === 0) return portalText("core", "account_admin.one_shared_46b45f");
    if (dedicated === 1) return portalText("core", "account_admin.one_shared_one_dedicated_976163");
    return portalText("core", "account_admin.one_shared_dedicated_ed7df6", { value0: String(dedicated) });
  }
  return dedicated === 1 ? portalText("core", "account_admin.one_dedicated_398d0f") : portalText("core", "account_admin._dedicated_81b000", { value0: String(dedicated) });
}

/** The prototype's fourth stat: the shared balance's plan and its talk rate — `null` while unknown or not readable. */
function usePlanStat(accountId: string | null, canRead: boolean): { readonly label: string; readonly value: string; readonly note: string } | null {
  const gateway = useGateway();
  const rates = useTalkRates();
  const enabled = accountId !== null && canRead;
  const offers = useQuery({ queryKey: ["commercial-offers", accountId ?? "none"], queryFn: () => gateway.listCommercialOffers(accountId ?? ""), enabled });
  const balances = useQuery({ queryKey: ["billing-accounts", accountId ?? "none"], queryFn: () => gateway.listBillingAccounts(accountId ?? ""), enabled });
  const shared = balances.data?.find((one) => one.scope === "account") ?? null;
  const subscription = useQuery({
    queryKey: ["balance-subscription", shared?.billingAccountId ?? "none"],
    queryFn: () => gateway.readBalanceSubscription(shared?.billingAccountId ?? ""),
    enabled: enabled && shared !== null,
  });
  if (offers.data === undefined || balances.data === undefined || (shared !== null && subscription.data === undefined)) return null;
  const plan = currentPlanOf(offers.data, subscription.data ?? null);
  const note = planRateNoteOf(rates.data?.talkRates ?? null);
  return { label: portalText("core", "account_admin.plan_ae2f98"), value: plan.label, note };
}

export function AccountOverviewScreen(_props: AccountOverviewScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const open = useOpenAccount();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const profile = useQuery({
    queryKey: accountProfileKey(open.accountId ?? "none"),
    queryFn: () => gateway.readAccountProfile(open.accountId ?? ""),
    enabled: open.accountId !== null,
  });
  const readsFinance = roleHolds(open.accountRole, "account.finance.read");
  const planStat = usePlanStat(open.accountId, readsFinance);
  const balances = useQuery({
    queryKey: ["billing-accounts", open.accountId ?? "none"],
    queryFn: () => gateway.listBillingAccounts(open.accountId ?? ""),
    enabled: open.accountId !== null && readsFinance,
  });

  const lede = portalText("core", "account_admin.one_organization_is_one_legal_payer_it_holds_your_workspaces_the_21d80b");
  if (open.accountId === null) {
    return (
      <OrganizationPage tab="overview" title={portalText("core", "account_admin.organization_519255")} lede={lede}>
        <Block>
          <BlockNote>{open.loading ? portalText("core", "account_admin.loading_your_organization_a01331") : portalText("core", "account_admin.choose_an_organization_first_066988")}</BlockNote>
        </Block>
      </OrganizationPage>
    );
  }
  if (profile.isPending) {
    return (
      <OrganizationPage tab="overview" title={portalText("core", "account_admin.organization_519255")} lede={lede}>
        <Block>
          <BlockNote>{portalText("core", "account_admin.loading_your_organization_a01331")}</BlockNote>
        </Block>
      </OrganizationPage>
    );
  }
  if (profile.isError) {
    return (
      <OrganizationPage tab="overview" title={portalText("core", "account_admin.organization_519255")} lede={lede}>
        <Block>
          <BlockNote>{apiProblemText(profile.error, portalText("core", "account_admin.this_organization_could_not_be_loaded_587b6c"))}</BlockNote>
        </Block>
      </OrganizationPage>
    );
  }
  const view = profile.data;
  const workspace = workspaceStats(view);
  const stat = [
    { label: portalText("core", "account_admin.workspaces_205b45"), ...workspace },
    { label: portalText("core", "account_admin.people_b37554"), value: String(view.personCount), note: portalText("core", "account_admin._in_organization_roles_d0cada", { value0: String(view.accountRoleCount) }) },
    ...(view.balanceCount === null ? [] : [{ label: portalText("core", "account_admin.balances_6047c3"), value: String(view.balanceCount), note: balanceSplitNoteOf(balances.data ?? null, view.balanceCount) }]),
    ...(planStat === null ? [] : [planStat]),
  ];
  return (
    <OrganizationPage tab="overview" title={portalText("core", "account_admin.organization_519255")} lede={lede} outcome={outcome}>
      <Block label={portalText("core", "account_admin.summary_12b71c")}>
        <StatGrid stat={stat} />
      </Block>
      <Block title={portalText("core", "account_admin.profile_ff4fc0")} dek={portalText("core", "account_admin.shown_to_your_team_legal_and_tax_details_live_under_documents_5b4fcd")}>
        <ProfileForm
          key={view.revision}
          accountId={open.accountId}
          profile={view}
          canWrite={roleHolds(open.accountRole, "account.profile.write")}
          canTimezone={roleHolds(open.accountRole, "account.finance.manage")}
          onOutcome={setOutcome}
        />
      </Block>
    </OrganizationPage>
  );
}
